/**
 * WebSocket 连接封装
 *
 * 功能：
 *   - 自动重连（指数退避）
 *   - 心跳保活
 *   - 事件订阅/取消
 *   - 消息 JSON 自动序列化/反序列化
 *   - 连接状态查询
 *
 * 使用示例：
 *   import WebSocketClient from '@/utils/websocket'
 *   const ws = new WebSocketClient('ws://localhost:8080/ws')
 *   ws.on('message', (data) => console.log(data))
 *   ws.connect()
 *   ws.send({ type: 'chat', content: 'hello' })
 */

// ─── 常量 ────────────────────────────────────────────
const READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
}

const DEFAULT_OPTIONS = {
  // 重连
  reconnect: true,             // 是否自动重连
  reconnectInterval: 2000,     // 首次重连间隔（ms）
  maxReconnectInterval: 30000, // 最大重连间隔（ms）
  reconnectDecay: 1.5,         // 重连间隔增长系数
  maxReconnectAttempts: 0,     // 最大重连次数，0 表示无限
  // 心跳
  heartbeat: true,             // 是否发送心跳
  heartbeatInterval: 30000,    // 心跳间隔（ms）
  heartbeatPayload: 'ping',    // 心跳发送内容
  // 连接
  connectTimeout: 10000,       // 连接超时（ms）
  // 调试
  debug: false,
}

// ─── WebSocketClient ─────────────────────────────────
class WebSocketClient {
  /**
   * @param {string} url          WebSocket 地址
   * @param {object} [options]    配置项，与 DEFAULT_OPTIONS 合并
   */
  constructor(url, options = {}) {
    this.url = url
    this.options = { ...DEFAULT_OPTIONS, ...options }

    // 实例状态
    this.ws = null
    this.reconnectCount = 0
    this.reconnectTimer = null
    this.heartbeatTimer = null
    this.connectTimer = null
    this.manualClose = false       // 区分主动关闭 / 被动断开
    this._destroyed = false

    // 事件池：{ open: [fn1, fn2], message: [...], close: [...], error: [...] }
    this._listeners = {}
  }

  // ─── 公开 API ─────────────────────────────────────

  /** 建立连接 */
  connect() {
    if (this._destroyed) {
      this._log('实例已销毁，无法连接')
      return
    }
    // 避免重复连接
    if (this.ws && [READY_STATE.CONNECTING, READY_STATE.OPEN].includes(this.ws.readyState)) {
      this._log('正在连接或已连接，跳过')
      return
    }

    this.manualClose = false
    this._cleanup()

    this.ws = new WebSocket(this.url)
    this._log('正在连接...')

    // 连接超时
    if (this.options.connectTimeout > 0) {
      this.connectTimer = setTimeout(() => {
        if (this.ws && this.ws.readyState === READY_STATE.CONNECTING) {
          this._log('连接超时')
          // 不在 CONNECTING 状态调用 close()，否则浏览器会抛出
          // "WebSocket is closed before the connection is established" 警告
          this.manualClose = true
          this._cleanup()
          // 连接最终会自然失败，onclose 中 manualClose=true 会阻止重连
        }
      }, this.options.connectTimeout)
    }

    this.ws.onopen = (event) => {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
      this.reconnectCount = 0
      this._log('已连接')
      if (this.options.heartbeat) {
        this._startHeartbeat()
      }
      this._emit('open', event)
    }

    this.ws.onmessage = (event) => {
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        data = event.data
      }
      this._emit('message', data)
    }

    this.ws.onclose = (event) => {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
      this._stopHeartbeat()
      this._emit('close', event)

      if (!this.manualClose && this.options.reconnect) {
        this._scheduleReconnect()
      }
    }

    this.ws.onerror = (event) => {
      this._emit('error', event)
    }
  }

  /**
   * 发送消息
   * @param {*} data  可序列化的数据，字符串则直接发送
   * @returns {boolean} 是否成功送入发送队列
   */
  send(data) {
    if (!this.ws || this.ws.readyState !== READY_STATE.OPEN) {
      this._log('未连接，发送失败', 'warn')
      return false
    }
    const payload = typeof data === 'string' ? data : JSON.stringify(data)
    this.ws.send(payload)
    return true
  }

  /** 关闭连接 */
  close(code = 1000, reason = '客户端主动关闭') {
    this.manualClose = true
    this._cleanup()
    if (this.ws) {
      const state = this.ws.readyState
      // 仅在 OPEN 或 CLOSING 状态才调用原生 close()，
      // 避免 CONNECTING 状态下触发浏览器警告
      if (state === READY_STATE.OPEN || state === READY_STATE.CLOSING) {
        try {
          this.ws.close(code, reason)
        } catch (_) { /* 忽略关闭时的异常 */ }
      }
    }
    this.ws = null
    this.reconnectCount = 0
  }

  /** 销毁实例（关闭连接并移除所有事件） */
  destroy() {
    this._destroyed = true
    this.close()
    this._listeners = {}
  }

  /**
   * 订阅事件
   * @param {'open'|'message'|'close'|'error'} event
   * @param {function} callback
   */
  on(event, callback) {
    if (!this._listeners[event]) {
      this._listeners[event] = []
    }
    this._listeners[event].push(callback)
  }

  /**
   * 取消订阅
   * @param {'open'|'message'|'close'|'error'} event
   * @param {function} callback  不传则取消该事件全部回调
   */
  off(event, callback) {
    if (!callback) {
      delete this._listeners[event]
      return
    }
    const cbs = this._listeners[event]
    if (cbs) {
      this._listeners[event] = cbs.filter(fn => fn !== callback)
    }
  }

  /**
   * 订阅一次性事件
   * @param {'open'|'message'|'close'|'error'} event
   * @param {function} callback
   */
  once(event, callback) {
    const wrapper = (...args) => {
      callback(...args)
      this.off(event, wrapper)
    }
    this.on(event, wrapper)
  }

  // ─── 状态查询 ─────────────────────────────────────

  /** 是否已连接 */
  get connected() {
    return !!(this.ws && this.ws.readyState === READY_STATE.OPEN)
  }

  /** 连接状态字符串 */
  get state() {
    if (!this.ws) return 'CLOSED'
    const map = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSING', 3: 'CLOSED' }
    return map[this.ws.readyState] || 'UNKNOWN'
  }

  // ─── 内部方法 ─────────────────────────────────────

  _emit(event, ...args) {
    const cbs = this._listeners[event]
    if (cbs && cbs.length) {
      cbs.forEach(fn => {
        try { fn(...args) } catch (e) { /* 防止一个回调报错影响其他 */ }
      })
    }
  }

  _scheduleReconnect() {
    const { reconnect, maxReconnectAttempts, maxReconnectInterval, reconnectInterval, reconnectDecay } = this.options
    if (!reconnect) return
    if (maxReconnectAttempts > 0 && this.reconnectCount >= maxReconnectAttempts) {
      this._log(`已达最大重连次数（${maxReconnectAttempts}），停止重连`, 'warn')
      return
    }

    this.reconnectCount++
    // 指数退避
    const delay = Math.min(
      reconnectInterval * Math.pow(reconnectDecay, this.reconnectCount - 1),
      maxReconnectInterval
    )

    this._log(`第 ${this.reconnectCount} 次重连，等待 ${Math.round(delay)}ms...`)
    this.reconnectTimer = setTimeout(() => {
      if (!this._destroyed && !this.manualClose) {
        this.connect()
      }
    }, delay)
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    const { heartbeatInterval, heartbeatPayload } = this.options
    this.heartbeatTimer = setInterval(() => {
      if (this.connected) {
        this.send(heartbeatPayload)
      }
    }, heartbeatInterval)
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  _cleanup() {
    clearTimeout(this.reconnectTimer)
    clearTimeout(this.connectTimer)
    this.reconnectTimer = null
    this.connectTimer = null
    this._stopHeartbeat()
  }

  _log(msg, level = 'log') {
    if (this.options.debug) {
      console[level](`[WebSocketClient] ${msg}`)
    }
  }
}

export default WebSocketClient
export { READY_STATE }
